import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  Divider,
  TextField,
  Stack,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import { grey, blue } from '@mui/material/colors';
import {
  Assessment,
  CheckCircle,
  Save,
  VerifiedUser,
  Analytics,
  ErrorOutline,
  Edit,
  Description,
  EmojiObjects,
  Notes,
  Info,
  FiberManualRecord,
  AccountTree,
  LinkOff,
  Hub
} from '@mui/icons-material';
import { analysisApi } from '../../api/analysisApi';
import { getPendingRequirements } from '../../api/masterRequirementsApi';
import useSmartAnalysis from '../../hooks/useSmartAnalysis';
import SmartAnalysisDialog from './SmartAnalysisDialog';
import GraphViewDialog from './GraphViewDialog';
import { SmartAssessmentCompact } from '../SmartAssessment';

const MAX_DETAIL_CACHE_ENTRIES = 120;
const VALUE_PREVIEW_CHARS = 220;

const KNOWN_RELATIONSHIP_LABELS = {
  is_verification_of: 'Is Verification Of',
  has_source: 'Has Source',
  is_derived_from: 'Is Derived From',
  refers_to: 'Refers To',
};

const KNOWN_RELATIONSHIP_ORDER = Object.keys(KNOWN_RELATIONSHIP_LABELS);

const STRUCTURED_PAYLOAD_KEYS = new Set([
  'text',
  'label',
  'notation',
  'status',
  'types',
  'relationships',
  'verification_plans',
  'verificationplan',
  'incoming_relationships',
  'constraints',
  'risks',
  'classification',
  'extra_properties',
]);

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [value];
};

const toObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }
  return {};
};

const toStringValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const humanizeKey = (key) => {
  if (!key) return 'Unknown';
  return String(key)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/:/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => {
      if (part.toLowerCase() === 'rdf') return 'RDF';
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
};

const normalizeRelationships = (relationships) => {
  const relObj = toObject(relationships);
  const normalized = {};
  Object.entries(relObj).forEach(([key, value]) => {
    normalized[key] = toArray(value);
  });
  return normalized;
};

const getAliasedValue = (sources, aliases, defaultValue) => {
  for (const source of sources) {
    for (const alias of aliases) {
      if (source && source[alias] !== undefined && source[alias] !== null) {
        return source[alias];
      }
    }
  }
  return defaultValue;
};

const normalizeRequirementData = (requirement) => {
  if (!requirement) return null;

  const payload = toObject(requirement.requirement_payload);
  const sources = [payload, requirement];

  const relationships = normalizeRelationships(
    getAliasedValue(sources, ['relationships'], {})
  );

  const orderedRelationshipEntries = [
    ...KNOWN_RELATIONSHIP_ORDER
      .filter((key) => Object.prototype.hasOwnProperty.call(relationships, key))
      .map((key) => [key, toArray(relationships[key])]),
    ...Object.keys(relationships)
      .filter((key) => !KNOWN_RELATIONSHIP_ORDER.includes(key))
      .sort((a, b) => a.localeCompare(b))
      .map((key) => [key, toArray(relationships[key])]),
  ];

  const visibleRelationshipEntries = orderedRelationshipEntries
    .map(([key, targets]) => [key, toArray(targets)])
    .filter(([, targets]) => targets.length > 0);

  const relationshipCount = visibleRelationshipEntries.reduce(
    (sum, [, targets]) => sum + targets.length,
    0
  );

  const verificationPlans = toArray(
    getAliasedValue(sources, ['verification_plans', 'verificationplan'], [])
  );

  const incomingRelationships = toArray(
    getAliasedValue(sources, ['incoming_relationships'], [])
  );

  const types = toArray(getAliasedValue(sources, ['types'], []));
  const constraints = toArray(getAliasedValue(sources, ['constraints'], []));
  const risks = toArray(getAliasedValue(sources, ['risks'], []));
  const extraProperties = toObject(getAliasedValue(sources, ['extra_properties'], {}));

  const classificationValue = getAliasedValue(sources, ['classification'], null);
  const classification = classificationValue && typeof classificationValue === 'object'
    ? classificationValue
    : classificationValue;

  const status = getAliasedValue(sources, ['status'], requirement.status || '');

  const unmappedStructuredFields = Object.entries(payload).filter(
    ([key]) => !STRUCTURED_PAYLOAD_KEYS.has(key)
  );

  return {
    relationships: visibleRelationshipEntries,
    relationshipCount,
    verificationPlans,
    incomingRelationships,
    types,
    constraints,
    risks,
    classification,
    status,
    extraProperties,
    unmappedStructuredFields,
  };
};

const getVerificationPlanMainFields = (plan) => {
  const planObj = toObject(plan);
  return {
    id: planObj['@id'] || planObj.id || '',
    phase: planObj['tennet:phase'] || planObj.phase || '',
    verificationMethod: planObj['tennet:verificationMethod'] || planObj.verificationMethod || [],
    hasAsSubject: planObj['tennet:hasAsSubject'] || planObj.hasAsSubject || '',
    isVerificationOf: planObj['tennet:isVerificationOf'] || planObj.isVerificationOf || '',
    rdfType: planObj['rdf:type'] || planObj['@type'] || '',
  };
};

/**
 * Analysis results dialog
 */
const AnalysisDialog = ({ 
  open, 
  analysis, 
  onClose, 
  loading = false,
  onSmartAnalysisSuccess,
  hasPermission = true 
}) => {
  // State for tracking edited requirements
  const [editedRequirements, setEditedRequirements] = useState({});
  const [originalRequirements, setOriginalRequirements] = useState({});
  const [savingStates, setSavingStates] = useState({});
  const [dirtyStates, setDirtyStates] = useState({});
  const [requirements, setRequirements] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [validatedDelta, setValidatedDelta] = useState(0);
  const detailCache = useRef(new Map());
  const hasAutoSelected = useRef(false);
  const infiniteScrollCbRef = useRef(null);
  const observerRef = useRef(null);
  const [smartAnalyzing, setSmartAnalyzing] = useState(false);
  const [smartError, setSmartError] = useState('');
  const [pendingValidationCount, setPendingValidationCount] = useState(0);
  const [smartDialogOpen, setSmartDialogOpen] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [graphDialogOpen, setGraphDialogOpen] = useState(false);
  
  // State for 2-panel layout
  const [selectedRequirementId, setSelectedRequirementId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedValues, setExpandedValues] = useState({});

  // Load pending validation count when dialog opens
  useEffect(() => {
    const loadPending = async () => {
      try {
        const data = await getPendingRequirements(1, 1);
        setPendingValidationCount(data.pagination?.total_items || 0);
      } catch (error) {
        console.error('Error loading pending requirements', error);
      }
    };

    if (open) {
      loadPending();
    }
  }, [open]);

  // Use SMART analysis hook
  const { handleStartSmartAnalysis } = useSmartAnalysis();

  useEffect(() => {
    if (analysis?.data?.requirements || analysis?.requirements) {
      const reqs = analysis?.data?.requirements || analysis?.requirements || [];
      const pag = analysis?.data?.pagination || null;
      setRequirements(reqs);
      setPagination(pag);
      setCurrentPage(pag?.page || 1);
      setValidatedDelta(0);
      // Reset caches and selection for new analysis load
      detailCache.current = new Map();
      hasAutoSelected.current = false;
      setSelectedRequirementId(null);
      setEditedRequirements({});
      setOriginalRequirements({});
      setDirtyStates({});
    }
  }, [analysis]);

  // Auto-select first requirement on initial load (lazy — fetches detail)
  useEffect(() => {
    if (!hasAutoSelected.current && requirements.length > 0) {
      hasAutoSelected.current = true;
      handleRequirementSelect(requirements[0].id);
    }
  }, [requirements]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute dirty state by comparing edited vs original
  useEffect(() => {
    const newDirtyStates = {};
    Object.keys(editedRequirements).forEach(reqId => {
      const edited = editedRequirements[reqId];
      const original = originalRequirements[reqId];
      
      if (edited && original) {
        const isDirty = 
          edited.label_english !== original.label_english ||
          edited.requirement_text_english !== original.requirement_text_english ||
          edited.notes !== original.notes ||
          edited.is_validated !== original.is_validated ||
          // SMART fields comparison
          edited.smart_specific !== original.smart_specific ||
          edited.smart_measurable !== original.smart_measurable ||
          edited.smart_achievable !== original.smart_achievable ||
          edited.smart_relevant !== original.smart_relevant ||
          edited.smart_time_bound !== original.smart_time_bound ||
          edited.smart_specific_override !== original.smart_specific_override ||
          edited.smart_specific_justification !== original.smart_specific_justification ||
          edited.smart_measurable_override !== original.smart_measurable_override ||
          edited.smart_measurable_justification !== original.smart_measurable_justification ||
          edited.smart_achievable_override !== original.smart_achievable_override ||
          edited.smart_achievable_justification !== original.smart_achievable_justification ||
          edited.smart_relevant_override !== original.smart_relevant_override ||
          edited.smart_relevant_justification !== original.smart_relevant_justification ||
          edited.smart_time_bound_override !== original.smart_time_bound_override ||
          edited.smart_time_bound_justification !== original.smart_time_bound_justification;
        
        newDirtyStates[reqId] = isDirty;
      } else {
        newDirtyStates[reqId] = false;
      }
    });
    setDirtyStates(newDirtyStates);
  }, [editedRequirements, originalRequirements]);

  // Initialize edit state for a requirement (called when detail is first loaded)
  const initEditState = (req) => {
    const reqData = {
      label_english: req.label_english || '',
      requirement_text_english: req.requirement_text_english || '',
      notes: req.notes || '',
      is_validated: req.is_validated || false,
      smart_specific: req.smart_specific || 0,
      smart_measurable: req.smart_measurable || 0,
      smart_achievable: req.smart_achievable || 0,
      smart_relevant: req.smart_relevant || 0,
      smart_time_bound: req.smart_time_bound || 0,
      smart_specific_reasoning: req.smart_specific_reasoning || '',
      smart_measurable_reasoning: req.smart_measurable_reasoning || '',
      smart_achievable_reasoning: req.smart_achievable_reasoning || '',
      smart_relevant_reasoning: req.smart_relevant_reasoning || '',
      smart_time_bound_reasoning: req.smart_time_bound_reasoning || '',
      smart_specific_override: req.smart_specific_override || false,
      smart_specific_justification: req.smart_specific_justification || '',
      smart_measurable_override: req.smart_measurable_override || false,
      smart_measurable_justification: req.smart_measurable_justification || '',
      smart_achievable_override: req.smart_achievable_override || false,
      smart_achievable_justification: req.smart_achievable_justification || '',
      smart_relevant_override: req.smart_relevant_override || false,
      smart_relevant_justification: req.smart_relevant_justification || '',
      smart_time_bound_override: req.smart_time_bound_override || false,
      smart_time_bound_justification: req.smart_time_bound_justification || ''
    };
    setEditedRequirements(prev => ({ ...prev, [req.id]: { ...reqData } }));
    setOriginalRequirements(prev => ({ ...prev, [req.id]: { ...reqData } }));
  };

  const handleFieldChange = (requirementId, field, value) => {
    setEditedRequirements(prev => ({
      ...prev,
      [requirementId]: {
        ...prev[requirementId],
        [field]: value
      }
    }));
  };

  const putDetailInCache = useCallback((requirementId, detail) => {
    if (!detail) return;

    if (detailCache.current.has(requirementId)) {
      detailCache.current.delete(requirementId);
    }
    detailCache.current.set(requirementId, detail);

    if (detailCache.current.size > MAX_DETAIL_CACHE_ENTRIES) {
      const oldestKey = detailCache.current.keys().next().value;
      detailCache.current.delete(oldestKey);
    }
  }, []);

  const handleSaveChanges = async (requirement) => {
    const requirementId = requirement.id;
    
    // Validate SMART overrides have justifications
    const edited = editedRequirements[requirementId];
    const missingJustifications = [];
    
    if (edited?.smart_specific_override && !edited?.smart_specific_justification?.trim()) {
      missingJustifications.push('Specific');
    }
    if (edited?.smart_measurable_override && !edited?.smart_measurable_justification?.trim()) {
      missingJustifications.push('Measurable');
    }
    if (edited?.smart_achievable_override && !edited?.smart_achievable_justification?.trim()) {
      missingJustifications.push('Achievable');
    }
    if (edited?.smart_relevant_override && !edited?.smart_relevant_justification?.trim()) {
      missingJustifications.push('Relevant');
    }
    if (edited?.smart_time_bound_override && !edited?.smart_time_bound_justification?.trim()) {
      missingJustifications.push('Time-bound');
    }
    
    // If there are missing justifications, show error dialog and don't save
    if (missingJustifications.length > 0) {
      setValidationErrors(missingJustifications);
      setValidationDialogOpen(true);
      return;
    }
    
    setSavingStates(prev => ({ ...prev, [requirementId]: true }));

    try {
      const payload = {
        label_english: edited?.label_english || '',
        requirement_text_english: edited?.requirement_text_english || '',
        notes: edited?.notes || '',
        is_validated: edited?.is_validated || false,
        // SMART fields
        smart_specific: edited?.smart_specific || 0,
        smart_measurable: edited?.smart_measurable || 0,
        smart_achievable: edited?.smart_achievable || 0,
        smart_relevant: edited?.smart_relevant || 0,
        smart_time_bound: edited?.smart_time_bound || 0,
        smart_specific_override: edited?.smart_specific_override || false,
        smart_specific_justification: edited?.smart_specific_justification || '',
        smart_measurable_override: edited?.smart_measurable_override || false,
        smart_measurable_justification: edited?.smart_measurable_justification || '',
        smart_achievable_override: edited?.smart_achievable_override || false,
        smart_achievable_justification: edited?.smart_achievable_justification || '',
        smart_relevant_override: edited?.smart_relevant_override || false,
        smart_relevant_justification: edited?.smart_relevant_justification || '',
        smart_time_bound_override: edited?.smart_time_bound_override || false,
        smart_time_bound_justification: edited?.smart_time_bound_justification || ''
      };
      
      console.log('[handleSaveChanges] Payload:', payload);

      const response = await analysisApi.updateRequirement(requirementId, payload);

      console.log('[handleSaveChanges] Success response:', response);

      // Update light requirements list (only is_validated matters for the list view)
      setRequirements(prev =>
        prev.map(req =>
          req.id === requirementId
            ? { ...req, is_validated: payload.is_validated }
            : req
        )
      );

      // Update detail cache with saved values
      if (detailCache.current.has(requirementId)) {
        putDetailInCache(requirementId, {
          ...detailCache.current.get(requirementId),
          ...payload
        });
      }

      // Track validated delta for server-side aggregate
      const originalValidated = originalRequirements[requirementId]?.is_validated;
      const newValidated = payload.is_validated;
      if (originalValidated !== newValidated) {
        setValidatedDelta(prev => prev + (newValidated ? 1 : -1));
      }

      // Update original values to match saved values (resets dirty state)
      setOriginalRequirements(prev => ({
        ...prev,
        [requirementId]: { ...payload }
      }));

      setSavingStates(prev => ({ ...prev, [requirementId]: false }));
    } catch (error) {
      console.error('[handleSaveChanges] Error:', error);
      console.error('[handleSaveChanges] Status:', error.response?.status);
      console.error('[handleSaveChanges] Response:', error.response?.data);
      setSavingStates(prev => ({ ...prev, [requirementId]: false }));
    }
  };

  const handleToggleValidation = (requirement) => {
    const requirementId = requirement.id;
    const newValidationState = !editedRequirements[requirementId]?.is_validated;

    console.log(`[handleToggleValidation] Toggling validation for ${requirementId} to ${newValidationState}`);

    // Update form state only (don't call backend yet)
    setEditedRequirements(prev => ({
      ...prev,
      [requirementId]: {
        ...prev[requirementId],
        is_validated: newValidationState
      }
    }));
  };

  // Derive fileId from analysis prop — needed by hooks below (must be before early returns)
  const fileIdForHooks = analysis?.data?.file_id || analysis?.file_id || null;

  // Load more requirements for infinite scroll (hook — must be before early returns)
  const loadMoreRequirements = useCallback(async (page) => {
    if (!fileIdForHooks) return;
    setLoadingMore(true);
    try {
      const response = await analysisApi.getFileAnalysis(fileIdForHooks, {
        page,
        page_size: pagination?.page_size || 50
      });
      const data = response?.data;
      if (data) {
        setRequirements(prev => [...prev, ...(data.requirements || [])]);
        setPagination(data.pagination || null);
        setCurrentPage(data.pagination?.page || page);
      }
    } catch (err) {
      console.error('[loadMoreRequirements] Error:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [fileIdForHooks, pagination?.page_size]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep infiniteScrollCbRef always fresh — no deps so it runs after every render
  useEffect(() => {
    infiniteScrollCbRef.current = () => {
      if (pagination?.has_next && !loadingMore) {
        loadMoreRequirements(currentPage + 1);
      }
    };
  });

  // Callback ref for the sentinel div — connects the observer when the node mounts,
  // disconnects it when it unmounts. Fires reliably regardless of dialog open/loading state.
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && infiniteScrollCbRef.current) {
          infiniteScrollCbRef.current();
        }
      },
      { threshold: 0.1 }
    );
    observerRef.current.observe(node);
  }, []); // stable — no deps needed, reads state via refs

  const analysisData = analysis?.data || analysis;
  const fileAnalysis = analysisData?.analysis;
  const totalCount = pagination?.total_count || fileAnalysis?.requirements_count || 0;

  // Server-side aggregates (no client-side filtering of all requirements)
  const translatedCount = fileAnalysis?.translated_count ?? 0;
  const savedValidatedCount = (fileAnalysis?.validated_count ?? 0) + validatedDelta;
  const smartAnalyzedCount = fileAnalysis?.smart_analyzed_count ?? 0;

  // Unsaved validated delta (requirements opened and toggled but not yet saved)
  const unsavedValidatedCount = Object.keys(editedRequirements).reduce((count, reqId) => {
    const edited = editedRequirements[reqId];
    const original = originalRequirements[reqId];
    if (edited && original && edited.is_validated !== original.is_validated) {
      return count + (edited.is_validated ? 1 : -1);
    }
    return count;
  }, 0);

  // Handle SMART analysis for validated requirements
  const handleAnalyzeValidatedRequirements = async () => {
    const fileId = analysisData?.file_id;
    
    if (!fileId) {
      setSmartError('File ID not found');
      return;
    }

    setSmartError('');
    setSmartAnalyzing(true);

    try {
      await handleStartSmartAnalysis(fileId, true); // true = validated_only
      setSmartDialogOpen(false); // Close SMART dialog
      
      // Call success callback to show message and switch to Repository tab
      if (onSmartAnalysisSuccess) {
        onSmartAnalysisSuccess();
      }
    } catch (error) {
      setSmartError(error.message || 'Error starting SMART analysis');
    } finally {
      setSmartAnalyzing(false);
    }
  };

  const handleOpenSmartDialog = () => {
    setSmartError('');
    setSmartDialogOpen(true);
  };

  const handleCloseSmartDialog = () => {
    if (!smartAnalyzing) {
      setSmartError('');
      setSmartDialogOpen(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    setExpandedValues({});
  }, [selectedRequirementId, activeTab]);

  const handleRequirementSelect = async (requirementId) => {
    setActiveTab(0);

    // Cache hit — use already-loaded detail
    if (detailCache.current.has(requirementId)) {
      setSelectedRequirementId(requirementId);
      if (!editedRequirements[requirementId]) {
        initEditState(detailCache.current.get(requirementId));
      }
      return;
    }

    // Cache miss — fetch full detail from backend
    setLoadingDetail(true);
    setSelectedRequirementId(requirementId);
    try {
      const detail = await analysisApi.getRequirementDetail(requirementId);
      if (detail) {
        putDetailInCache(requirementId, detail);
        initEditState(detail);
      }
    } catch (err) {
      console.error('[handleRequirementSelect] Error fetching detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const selectedRequirement = selectedRequirementId
    ? (detailCache.current.get(selectedRequirementId) || null)
    : null;

  const normalizedRequirement = useMemo(
    () => normalizeRequirementData(selectedRequirement),
    [selectedRequirement]
  );

  const relationshipCount = normalizedRequirement?.relationshipCount || 0;

  const toggleExpandedValue = useCallback((token) => {
    setExpandedValues((prev) => ({
      ...prev,
      [token]: !prev[token],
    }));
  }, []);

  const renderExpandableValue = useCallback((value, token, typographySx = {}) => {
    const textValue = toStringValue(value);
    const hasText = textValue.trim().length > 0;
    const isLong = textValue.length > VALUE_PREVIEW_CHARS;
    const expanded = Boolean(expandedValues[token]);
    const displayValue = isLong && !expanded
      ? `${textValue.slice(0, VALUE_PREVIEW_CHARS)}...`
      : textValue;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography
          variant="caption"
          sx={{
            color: grey[800],
            wordBreak: 'break-all',
            whiteSpace: 'pre-wrap',
            ...typographySx,
          }}
        >
          {hasText ? displayValue : '-'}
        </Typography>
        {isLong && (
          <Button
            size="small"
            onClick={() => toggleExpandedValue(token)}
            sx={{
              alignSelf: 'flex-start',
              minWidth: 0,
              px: 0,
              py: 0,
              textTransform: 'none',
              fontSize: '0.72rem',
              lineHeight: 1.3,
            }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        )}
      </Box>
    );
  }, [expandedValues, toggleExpandedValue]);

  if (!analysis && !loading) return null;

  // Handle loading state
  if (analysis?.loading) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Typography variant="h5" component="div">
            Analysis Results
          </Typography>
        </DialogTitle>
        
        <DialogContent dividers>
          {hasPermission ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Typography color="error" variant="body1">
              You do not have permission to view this analysis.
            </Typography>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          height: { xs: '92dvh', md: '88dvh' },
          maxHeight: { xs: '92dvh', md: '88dvh' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Assessment />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" component="div">
                Analysis Results
              </Typography>
              {pendingValidationCount > 0 && (
                <Chip
                  label={`${pendingValidationCount} pending validation`}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {analysisData?.file_name}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
            {/* Overview Statistics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Requirements
                    </Typography>
                    <Typography variant="h4">
                      {fileAnalysis?.requirements_count || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Translated
                    </Typography>
                    <Typography variant="h4">
                      {translatedCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {totalCount > 0 
                        ? `${Math.round(translatedCount / totalCount * 100)}%` 
                        : '0%'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Validated
                    </Typography>
                    <Typography variant="h4">
                      {savedValidatedCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {totalCount > 0 
                        ? `${Math.round(savedValidatedCount / totalCount * 100)}%` 
                        : '0%'}
                    </Typography>
                    {unsavedValidatedCount > 0 && (
                      <Typography variant="caption" display="block" sx={{ color: 'warning.main', mt: 0.5 }}>
                        +{unsavedValidatedCount} Unsaved
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      SMART Analyzed
                    </Typography>
                    <Typography variant="h4">
                      {smartAnalyzedCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {totalCount > 0 
                        ? `${Math.round(smartAnalyzedCount / totalCount * 100)}%` 
                        : '0%'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* {fileAnalysis && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    File Statistics
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Total Triples
                      </Typography>
                      <Typography variant="body1">
                        {fileAnalysis.total_triples || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Unique Subjects
                      </Typography>
                      <Typography variant="body1">
                        {fileAnalysis.unique_subjects || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Unique Objects
                      </Typography>
                      <Typography variant="body1">
                        {fileAnalysis.unique_objects || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )} */}

            {/* 2-Panel Layout: Requirements List + Detail View */}
            <Box sx={{ mt: 1, width: '100%', flex: 1, height: 0, minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
              <Grid
                container
                columnSpacing={2}
                rowSpacing={0}
                sx={{
                  height: '100%',
                  maxHeight: '100%',
                  width: '100%',
                  minHeight: 0,
                  alignItems: 'stretch',
                  alignContent: 'stretch',
                  overflow: 'hidden',
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                }}
              >
                {/* LEFT PANEL: Requirements List (20%) */}
                <Grid item xs={12} md={3} sx={{ height: '100%', minHeight: 0, maxHeight: '100%', display: 'flex', overflow: 'hidden' }}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      height: { xs: '100%', md: '100%' },
                      maxHeight: { xs: '100%', md: '100%' },
                      width: '100%',
                      minHeight: 0,
                      display: 'flex', 
                      flexDirection: 'column',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Requirements ({requirements.length}{totalCount > requirements.length ? ` / ${totalCount}` : ''})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Select a requirement to view details
                      </Typography>
                    </Box>
                    
                    <List 
                      sx={{ 
                        flex: 1, 
                        height: 0,
                        minHeight: 0,
                        maxHeight: '100%',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        overscrollBehavior: 'contain',
                        p: 0,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          bgcolor: grey[100],
                        },
                        '&::-webkit-scrollbar-thumb': {
                          bgcolor: grey[400],
                          borderRadius: '4px',
                          '&:hover': {
                            bgcolor: grey[600],
                          },
                        },
                      }}
                    >
                      {requirements.map((req, index) => (
                        <ListItemButton
                          key={req.id}
                          selected={selectedRequirementId === req.id}
                          onClick={() => handleRequirementSelect(req.id)}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            py: 2,
                            px: 3,
                            '&.Mui-selected': {
                              bgcolor: blue[50],
                              borderLeft: `4px solid ${blue[500]}`,
                              '&:hover': {
                                bgcolor: blue[100],
                              },
                            },
                            '&:hover': {
                              bgcolor: grey[50],
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontFamily: 'monospace',
                                    fontWeight: 600,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {req.notation || `Req ${index + 1}`}
                                </Typography>
                                {dirtyStates[req.id] && (
                                  <FiberManualRecord sx={{ fontSize: 8, color: 'warning.main' }} />
                                )}
                              </Box>
                            }
                            secondary={
                              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                                {req.translation_completed && (
                                  <Chip 
                                    label="Translated"
                                    size="small"
                                    sx={{ 
                                      height: 18,
                                      fontSize: '0.65rem',
                                      bgcolor: grey[200],
                                      color: grey[800]
                                    }}
                                  />
                                )}
                                {(req.is_validated || editedRequirements[req.id]?.is_validated) && (
                                  <Chip 
                                    label="Validated"
                                    size="small"
                                    sx={{ 
                                      height: 18,
                                      fontSize: '0.65rem',
                                      bgcolor: grey[200],
                                      color: grey[800]
                                    }}
                                  />
                                )}
                                {req.smart_analysis_completed && (
                                  <Chip 
                                    label="SMART"
                                    size="small"
                                    icon={<EmojiObjects sx={{ fontSize: 12 }} />}
                                    sx={{ 
                                      height: 18,
                                      fontSize: '0.65rem',
                                      bgcolor: blue[50],
                                      color: blue[700]
                                    }}
                                  />
                                )}
                                {req.relationship_count > 0 && (
                                  <Chip
                                    label={`${req.relationship_count} link${req.relationship_count !== 1 ? 's' : ''}`}
                                    size="small"
                                    icon={<AccountTree sx={{ fontSize: 10 }} />}
                                    sx={{
                                      height: 18,
                                      fontSize: '0.65rem',
                                      bgcolor: blue[50],
                                      color: blue[700]
                                    }}
                                  />
                                )}
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      ))}
                      {/* Infinite scroll sentinel */}
                      <div ref={sentinelRef} style={{ height: 1 }} />
                      {loadingMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      )}
                    </List>
                  </Paper>
                </Grid>

                {/* RIGHT PANEL: Requirement Details with Tabs (70%) */}
                <Grid item xs={12} md={9} sx={{ height: '100%', minHeight: 0, maxHeight: '100%', display: 'flex', overflow: 'hidden' }}>
                  {loadingDetail ? (
                    <Paper
                      elevation={0}
                      sx={{
                        height: '100%',
                        maxHeight: '100%',
                        width: '100%',
                        minHeight: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                      }}
                    >
                      <CircularProgress />
                    </Paper>
                  ) : selectedRequirement ? (
                    <Paper 
                      elevation={0}
                      sx={{ 
                        height: '100%',
                        maxHeight: '100%',
                        width: '100%',
                        minHeight: 0,
                        display: 'flex', 
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Header */}
                      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                              {selectedRequirement.notation || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {selectedRequirement.label || 'No label available'}
                            </Typography>
                          </Box>
                          
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            {dirtyStates[selectedRequirement.id] && (
                              <Chip 
                                label="Unsaved changes" 
                                size="small" 
                                color="warning"
                                sx={{ fontSize: '0.7rem', height: 24 }}
                              />
                            )}
                            {editedRequirements[selectedRequirement.id]?.is_validated && !dirtyStates[selectedRequirement.id] && (
                              <Chip
                                icon={<CheckCircle fontSize="small" />}
                                label="Validated"
                                size="small"
                                sx={{
                                  bgcolor: grey[200],
                                  color: grey[900],
                                  fontWeight: 600,
                                  border: `1px solid ${grey[300]}`,
                                  height: 24
                                }}
                              />
                            )}
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VerifiedUser />}
                              onClick={() => handleToggleValidation(selectedRequirement)}
                              sx={{
                                borderColor: grey[300],
                                color: grey[900],
                                fontWeight: 600,
                                '&:hover': {
                                  borderColor: grey[900],
                                  bgcolor: grey[50]
                                }
                              }}
                            >
                              {editedRequirements[selectedRequirement.id]?.is_validated ? 'Unvalidate' : 'Validate'}
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={savingStates[selectedRequirement.id] ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Save />}
                              onClick={() => handleSaveChanges(selectedRequirement)}
                              disabled={!dirtyStates[selectedRequirement.id] || savingStates[selectedRequirement.id]}
                              sx={{
                                bgcolor: grey[900],
                                color: 'white',
                                fontWeight: 600,
                                '&:hover': {
                                  bgcolor: grey[800]
                                },
                                '&.Mui-disabled': {
                                  bgcolor: grey[400],
                                  color: grey[100]
                                }
                              }}
                            >
                              {savingStates[selectedRequirement.id] ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </Stack>
                        </Box>
                      </Box>

                      {/* Tabs */}
                      <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        sx={{ 
                          flexShrink: 0,
                          borderBottom: '1px solid', 
                          borderColor: 'divider',
                          px: 2,
                          '& .MuiTab-root': {
                            minHeight: 48,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <Tab icon={<Info fontSize="small" />} iconPosition="start" label="Overview" />
                        <Tab icon={<Description fontSize="small" />} iconPosition="start" label="Text" />
                        <Tab icon={<EmojiObjects fontSize="small" />} iconPosition="start" label="SMART Analysis" />
                        <Tab icon={<Notes fontSize="small" />} iconPosition="start" label="Notes" />
                        <Tab
                          icon={<AccountTree fontSize="small" />}
                          iconPosition="start"
                          label={relationshipCount > 0
                            ? <Badge badgeContent={relationshipCount} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>Relationships</Badge>
                            : 'Relationships'}
                        />
                      </Tabs>

                      {/* Tab Content */}
                      <Box 
                        sx={{ 
                          flex: 1,
                          minHeight: 0,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          p: 3,
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            bgcolor: grey[100],
                          },
                          '&::-webkit-scrollbar-thumb': {
                            bgcolor: grey[400],
                            borderRadius: '4px',
                            '&:hover': {
                              bgcolor: grey[600],
                            },
                          },
                        }}
                      >
                        {/* OVERVIEW TAB */}
                        {activeTab === 0 && (
                          <Stack spacing={3}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                Notation
                              </Typography>
                              <Box sx={{ p: 1.5, bgcolor: grey[100], borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: grey[900] }}>
                                  {selectedRequirement.notation || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                Label (Original)
                              </Typography>
                              <Box sx={{ p: 1.5, bgcolor: grey[100], borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ color: grey[900] }}>
                                  {selectedRequirement.label || '-'}
                                </Typography>
                              </Box>
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                English Label
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                value={editedRequirements[selectedRequirement.id]?.label_english || ''}
                                onChange={(e) => handleFieldChange(selectedRequirement.id, 'label_english', e.target.value)}
                                variant="outlined"
                                placeholder="Enter English label..."
                              />
                            </Box>

                            {/* TYPES Section */}
                            {normalizedRequirement?.types?.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                  Types
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                  {normalizedRequirement.types.map((typeUri, i) => {
                                    const typeLabel = typeUri?.includes('/') ? typeUri.split('/').pop() : typeUri;
                                    return (
                                      <Chip
                                        key={i}
                                        label={typeLabel}
                                        size="small"
                                        sx={{ bgcolor: blue[50], color: blue[700], fontWeight: 500 }}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}

                            {/* STATUS Section */}
                            {normalizedRequirement?.status && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                  Status
                                </Typography>
                                <Chip
                                  label={normalizedRequirement.status}
                                  size="small"
                                  sx={{ bgcolor: grey[200], color: grey[800], fontWeight: 500 }}
                                />
                              </Box>
                            )}

                            {/* CLASSIFICATION Section */}
                            {normalizedRequirement?.classification && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                  Classification
                                </Typography>
                                {(() => {
                                  const classification = normalizedRequirement.classification;
                                  const isObject = classification && typeof classification === 'object' && !Array.isArray(classification);

                                  if (!isObject) {
                                    return (
                                      <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: 1, border: '1px solid', borderColor: grey[200] }}>
                                        <Typography variant="body2" sx={{ color: grey[900], wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                                          {toStringValue(classification)}
                                        </Typography>
                                      </Box>
                                    );
                                  }

                                  const broader = toArray(classification.broader);
                                  const narrower = toArray(classification.narrower);
                                  const rest = Object.entries(classification).filter(([key]) => !['broader', 'narrower'].includes(key));

                                  return (
                                    <Stack spacing={1}>
                                      {broader.length > 0 && (
                                        <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: 1, border: '1px solid', borderColor: grey[200] }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: grey[600], display: 'block', mb: 0.5 }}>
                                            Broader:
                                          </Typography>
                                          {broader.map((uri, i) => (
                                            <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: grey[800], wordBreak: 'break-all' }}>
                                              {toStringValue(uri)}
                                            </Typography>
                                          ))}
                                        </Box>
                                      )}
                                      {narrower.length > 0 && (
                                        <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: 1, border: '1px solid', borderColor: grey[200] }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: grey[600], display: 'block', mb: 0.5 }}>
                                            Narrower:
                                          </Typography>
                                          {narrower.map((uri, i) => (
                                            <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: grey[800], wordBreak: 'break-all' }}>
                                              {toStringValue(uri)}
                                            </Typography>
                                          ))}
                                        </Box>
                                      )}
                                      {rest.map(([key, value]) => (
                                        <Box key={key} sx={{ p: 1.5, bgcolor: grey[50], borderRadius: 1, border: '1px solid', borderColor: grey[200] }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: grey[600], display: 'block', mb: 0.5 }}>
                                            {humanizeKey(key)}:
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: grey[900], wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                                            {toStringValue(value)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  );
                                })()}
                              </Box>
                            )}

                            {/* CONSTRAINTS Section */}
                            {normalizedRequirement?.constraints?.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                  Constraints
                                </Typography>
                                <Stack spacing={1}>
                                  {normalizedRequirement.constraints.map((item, i) => (
                                    <Box key={i} sx={{ p: 1.5, bgcolor: grey[100], borderRadius: 1, borderLeft: '3px solid', borderColor: grey[400] }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: grey[900], wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                                        {toStringValue(item)}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            {/* RISKS Section */}
                            {normalizedRequirement?.risks?.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                  Risks
                                </Typography>
                                <Stack spacing={1}>
                                  {normalizedRequirement.risks.map((item, i) => (
                                    <Box key={i} sx={{ p: 1.5, bgcolor: grey[100], borderRadius: 1, borderLeft: '3px solid', borderColor: grey[400] }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: grey[900], wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                                        {toStringValue(item)}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Stack>
                        )}

                        {/* TEXT TAB */}
                        {activeTab === 1 && (
                          <Stack spacing={3}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                Original Text
                              </Typography>
                              <Box sx={{ p: 2, bgcolor: grey[100], borderRadius: 1, minHeight: 150 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: grey[900], lineHeight: 1.6 }}>
                                  {selectedRequirement.requirement_text || '-'}
                                </Typography>
                              </Box>
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                Translated Text (English)
                              </Typography>
                              <TextField
                                fullWidth
                                multiline
                                minRows={8}
                                value={editedRequirements[selectedRequirement.id]?.requirement_text_english || ''}
                                onChange={(e) => handleFieldChange(selectedRequirement.id, 'requirement_text_english', e.target.value)}
                                variant="outlined"
                                placeholder="Enter English translation..."
                              />
                            </Box>
                          </Stack>
                        )}

                        {/* SMART TAB */}
                        {activeTab === 2 && (
                          <Box>
                            {selectedRequirement.smart_analysis_completed ? (
                              <SmartAssessmentCompact
                                smartSpecific={editedRequirements[selectedRequirement.id]?.smart_specific || 0}
                                smartMeasurable={editedRequirements[selectedRequirement.id]?.smart_measurable || 0}
                                smartAchievable={editedRequirements[selectedRequirement.id]?.smart_achievable || 0}
                                smartRelevant={editedRequirements[selectedRequirement.id]?.smart_relevant || 0}
                                smartTimeBound={editedRequirements[selectedRequirement.id]?.smart_time_bound || 0}
                                smartSpecificReasoning={editedRequirements[selectedRequirement.id]?.smart_specific_reasoning || ''}
                                smartMeasurableReasoning={editedRequirements[selectedRequirement.id]?.smart_measurable_reasoning || ''}
                                smartAchievableReasoning={editedRequirements[selectedRequirement.id]?.smart_achievable_reasoning || ''}
                                smartRelevantReasoning={editedRequirements[selectedRequirement.id]?.smart_relevant_reasoning || ''}
                                smartTimeBoundReasoning={editedRequirements[selectedRequirement.id]?.smart_time_bound_reasoning || ''}
                                smartSpecificOverride={editedRequirements[selectedRequirement.id]?.smart_specific_override || false}
                                smartSpecificJustification={editedRequirements[selectedRequirement.id]?.smart_specific_justification || ''}
                                smartMeasurableOverride={editedRequirements[selectedRequirement.id]?.smart_measurable_override || false}
                                smartMeasurableJustification={editedRequirements[selectedRequirement.id]?.smart_measurable_justification || ''}
                                smartAchievableOverride={editedRequirements[selectedRequirement.id]?.smart_achievable_override || false}
                                smartAchievableJustification={editedRequirements[selectedRequirement.id]?.smart_achievable_justification || ''}
                                smartRelevantOverride={editedRequirements[selectedRequirement.id]?.smart_relevant_override || false}
                                smartRelevantJustification={editedRequirements[selectedRequirement.id]?.smart_relevant_justification || ''}
                                smartTimeBoundOverride={editedRequirements[selectedRequirement.id]?.smart_time_bound_override || false}
                                smartTimeBoundJustification={editedRequirements[selectedRequirement.id]?.smart_time_bound_justification || ''}
                                onSpecificChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_specific', value)}
                                onMeasurableChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_measurable', value)}
                                onAchievableChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_achievable', value)}
                                onRelevantChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_relevant', value)}
                                onTimeBoundChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_time_bound', value)}
                                onSpecificOverrideChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_specific_override', value)}
                                onSpecificJustificationChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_specific_justification', value)}
                                onMeasurableOverrideChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_measurable_override', value)}
                                onMeasurableJustificationChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_measurable_justification', value)}
                                onAchievableOverrideChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_achievable_override', value)}
                                onAchievableJustificationChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_achievable_justification', value)}
                                onRelevantOverrideChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_relevant_override', value)}
                                onRelevantJustificationChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_relevant_justification', value)}
                                onTimeBoundOverrideChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_time_bound_override', value)}
                                onTimeBoundJustificationChange={(value) => handleFieldChange(selectedRequirement.id, 'smart_time_bound_justification', value)}
                              />
                            ) : (
                              <Box sx={{ textAlign: 'center', py: 8 }}>
                                <EmojiObjects sx={{ fontSize: 64, color: grey[300], mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                  No SMART Analysis Available
                                </Typography>
                                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                                  This requirement has not been analyzed yet
                                </Typography>
                                <Alert severity="info" sx={{ mt: 2, maxWidth: 500, mx: 'auto' }}>
                                  Use the "Run Analysis" button in the Overview tab to perform SMART analysis on validated requirements.
                                </Alert>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* RELATIONSHIPS TAB */}
                        {activeTab === 4 && (
                          <Stack spacing={3}>
                            {relationshipCount === 0 ? (
                              <Box sx={{ textAlign: 'center', py: 8 }}>
                                <LinkOff sx={{ fontSize: 64, color: grey[300], mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                  No Relationships Found
                                </Typography>
                                <Typography variant="body2" color="text.disabled">
                                  This requirement has no extracted RDF relationships
                                </Typography>
                              </Box>
                            ) : (
                              normalizedRequirement?.relationships?.map(([key, targets]) => {
                                const label = KNOWN_RELATIONSHIP_LABELS[key] || humanizeKey(key);

                                return (
                                  <Box key={key}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ mb: 1.5, fontWeight: 600, color: grey[700], display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                      <AccountTree fontSize="small" sx={{ color: grey[500] }} />
                                      {label}
                                      <Chip
                                        label={targets.length}
                                        size="small"
                                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: grey[200], color: grey[800], ml: 0.5 }}
                                      />
                                    </Typography>
                                    <Stack spacing={1}>
                                      {targets.map((target, i) => {
                                        const isResolved = target && typeof target === 'object' && !Array.isArray(target);
                                        const targetId = isResolved ? (target['@id'] || target.id || '') : toStringValue(target);
                                        const typeRaw = isResolved ? (target['rdf:type'] || target['@type'] || '') : '';
                                        const typeLabel = toStringValue(typeRaw);
                                        const prefLabel = isResolved ? (target['skos:prefLabel'] || target.prefLabel || '') : '';
                                        const extraEntries = isResolved
                                          ? Object.entries(target).filter(([field]) => !['@id', 'id', 'rdf:type', '@type', 'skos:prefLabel', 'prefLabel'].includes(field))
                                          : [];

                                        return (
                                          <Box
                                            key={`${key}-${i}`}
                                            sx={{
                                              p: 1.5,
                                              bgcolor: isResolved ? blue[50] : grey[100],
                                              borderRadius: 1,
                                              borderLeft: '3px solid',
                                              borderColor: isResolved ? 'primary.main' : grey[400],
                                              overflow: 'hidden',
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: grey[600], wordBreak: 'break-all' }}
                                            >
                                              {targetId || '-'}
                                            </Typography>

                                            {isResolved && (
                                              <Box sx={{ mt: 0.75 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                  {typeLabel && (
                                                    <Chip
                                                      label={typeLabel}
                                                      size="small"
                                                      sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'primary.main', color: '#fff' }}
                                                    />
                                                  )}
                                                  {prefLabel && (
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: grey[900] }}>
                                                      {prefLabel}
                                                    </Typography>
                                                  )}
                                                </Box>

                                                {extraEntries.length > 0 && (
                                                  <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {extraEntries.map(([field, value]) => (
                                                      <Box key={`${key}-${i}-${field}`}>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block' }}>
                                                          {humanizeKey(field)}
                                                        </Typography>
                                                        {renderExpandableValue(value, `rel-${key}-${i}-${field}`)}
                                                      </Box>
                                                    ))}
                                                  </Box>
                                                )}
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </Stack>
                                  </Box>
                                );
                              })
                            )}

                            {/* VERIFICATION PLANS Section */}
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ mb: 1.5, fontWeight: 600, color: grey[700], display: 'flex', alignItems: 'center', gap: 1 }}
                              >
                                <AccountTree fontSize="small" sx={{ color: grey[500] }} />
                                Verification Plans
                                <Chip
                                  label={normalizedRequirement?.verificationPlans?.length || 0}
                                  size="small"
                                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: grey[200], color: grey[800], ml: 0.5 }}
                                />
                              </Typography>

                              {normalizedRequirement?.verificationPlans?.length ? (
                                <Stack spacing={1}>
                                  {normalizedRequirement.verificationPlans.map((plan, i) => {
                                    const isObjectPlan = plan && typeof plan === 'object' && !Array.isArray(plan);
                                    const planObj = toObject(plan);
                                    const main = getVerificationPlanMainFields(planObj);
                                    const extraEntries = isObjectPlan
                                      ? Object.entries(planObj).filter(([field]) => ![
                                        '@id',
                                        'id',
                                        'tennet:phase',
                                        'phase',
                                        'tennet:verificationMethod',
                                        'verificationMethod',
                                        'tennet:hasAsSubject',
                                        'hasAsSubject',
                                        'tennet:isVerificationOf',
                                        'isVerificationOf',
                                        'rdf:type',
                                        '@type',
                                      ].includes(field))
                                      : [];

                                    return (
                                      <Box
                                        key={`verification-plan-${i}`}
                                        sx={{
                                          p: 1.5,
                                          bgcolor: blue[50],
                                          borderRadius: 1,
                                          borderLeft: '3px solid',
                                          borderColor: 'primary.main',
                                        }}
                                      >
                                        {!isObjectPlan ? (
                                          renderExpandableValue(plan, `verification-plan-${i}-raw`, { fontFamily: 'monospace' })
                                        ) : (
                                          <Stack spacing={0.75}>
                                            <Box>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block' }}>
                                                ID
                                              </Typography>
                                              {renderExpandableValue(main.id, `verification-plan-${i}-id`, { fontFamily: 'monospace' })}
                                            </Box>
                                            <Box>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block' }}>
                                                Phase
                                              </Typography>
                                              {renderExpandableValue(main.phase, `verification-plan-${i}-phase`)}
                                            </Box>
                                            <Box>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block' }}>
                                                Verification Method
                                              </Typography>
                                              {renderExpandableValue(main.verificationMethod, `verification-plan-${i}-method`)}
                                            </Box>
                                            <Box>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block' }}>
                                                Has As Subject
                                              </Typography>
                                              {renderExpandableValue(main.hasAsSubject, `verification-plan-${i}-subject`, { fontFamily: 'monospace' })}
                                            </Box>
                                            <Box>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block' }}>
                                                Is Verification Of
                                              </Typography>
                                              {renderExpandableValue(main.isVerificationOf, `verification-plan-${i}-is-verification-of`, { fontFamily: 'monospace' })}
                                            </Box>
                                            <Box>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block' }}>
                                                RDF Type
                                              </Typography>
                                              {renderExpandableValue(main.rdfType, `verification-plan-${i}-rdf-type`)}
                                            </Box>

                                            {extraEntries.length > 0 && (
                                              <Box sx={{ mt: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block', mb: 0.25 }}>
                                                  Additional Verification Plan Fields
                                                </Typography>
                                                <Stack spacing={0.5}>
                                                  {extraEntries.map(([field, value]) => (
                                                    <Box key={`verification-plan-${i}-${field}`}>
                                                      <Typography variant="caption" sx={{ fontWeight: 600, color: grey[700], display: 'block' }}>
                                                        {humanizeKey(field)}
                                                      </Typography>
                                                      {renderExpandableValue(value, `verification-plan-${i}-extra-${field}`)}
                                                    </Box>
                                                  ))}
                                                </Stack>
                                              </Box>
                                            )}
                                          </Stack>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              ) : (
                                <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: 1, border: '1px dashed', borderColor: grey[300] }}>
                                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                    No verification plans found
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* INCOMING RELATIONSHIPS Section */}
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ mb: 1.5, fontWeight: 600, color: grey[700], display: 'flex', alignItems: 'center', gap: 1 }}
                              >
                                <AccountTree fontSize="small" sx={{ color: grey[500] }} />
                                Incoming Relationships
                                <Chip
                                  label={normalizedRequirement?.incomingRelationships?.length || 0}
                                  size="small"
                                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: grey[200], color: grey[800], ml: 0.5 }}
                                />
                              </Typography>

                              {normalizedRequirement?.incomingRelationships?.length ? (
                                <Stack spacing={1}>
                                  {normalizedRequirement.incomingRelationships.map((rel, i) => (
                                    <Box
                                      key={`incoming-${i}`}
                                      sx={{
                                        p: 1.5,
                                        bgcolor: grey[100],
                                        borderRadius: 1,
                                        borderLeft: '3px solid',
                                        borderColor: grey[400],
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {typeof rel === 'object' && rel !== null ? (
                                        <>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontFamily: 'monospace', fontWeight: 600, color: grey[600], display: 'block', mb: 0.5 }}
                                          >
                                            {rel?.predicate || 'predicate'}
                                          </Typography>
                                          {renderExpandableValue(rel?.from || rel?.source || rel, `incoming-${i}-from`, { fontFamily: 'monospace' })}
                                        </>
                                      ) : (
                                        renderExpandableValue(rel, `incoming-${i}-value`, { fontFamily: 'monospace' })
                                      )}
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: 1, border: '1px dashed', borderColor: grey[300] }}>
                                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                    No incoming relationships found
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* EXTRA PROPERTIES Section */}
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}
                              >
                                Additional Properties
                              </Typography>

                              {Object.keys(normalizedRequirement?.extraProperties || {}).length > 0 ? (
                                <Stack spacing={0.75}>
                                  {Object.entries(normalizedRequirement.extraProperties).map(([key, value]) => (
                                    <Box
                                      key={key}
                                      sx={{
                                        p: 1.25,
                                        bgcolor: grey[50],
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: grey[200],
                                        display: 'flex',
                                        gap: 1,
                                        flexWrap: 'wrap',
                                        alignItems: 'flex-start',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 600, color: grey[700], fontFamily: 'monospace', minWidth: 120, flexShrink: 0 }}
                                      >
                                        {key}:
                                      </Typography>
                                      <Box sx={{ flex: 1, minWidth: 180 }}>
                                        {renderExpandableValue(value, `extra-property-${key}`)}
                                      </Box>
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: 1, border: '1px dashed', borderColor: grey[300] }}>
                                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                    No additional properties found
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* UNMAPPED STRUCTURED FIELDS Section */}
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}
                              >
                                Unmapped Structured Fields
                              </Typography>

                              {normalizedRequirement?.unmappedStructuredFields?.length ? (
                                <Stack spacing={0.75}>
                                  {normalizedRequirement.unmappedStructuredFields.map(([key, value]) => (
                                    <Box
                                      key={`unmapped-${key}`}
                                      sx={{
                                        p: 1.25,
                                        bgcolor: grey[50],
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: grey[200],
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 600, color: grey[700], display: 'block', mb: 0.25 }}
                                      >
                                        {humanizeKey(key)} ({key})
                                      </Typography>
                                      {renderExpandableValue(value, `unmapped-${key}`)}
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Box sx={{ p: 1.5, bgcolor: grey[50], borderRadius: 1, border: '1px dashed', borderColor: grey[300] }}>
                                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                    No unmapped structured fields found
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Stack>
                        )}

                        {/* NOTES TAB */}
                        {activeTab === 3 && (
                          <Stack spacing={3}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: grey[700] }}>
                                Internal Notes
                              </Typography>
                              <TextField
                                fullWidth
                                multiline
                                minRows={12}
                                value={editedRequirements[selectedRequirement.id]?.notes || ''}
                                onChange={(e) => handleFieldChange(selectedRequirement.id, 'notes', e.target.value)}
                                variant="outlined"
                                placeholder="Add internal notes about this requirement..."
                              />
                            </Box>
                          </Stack>
                        )}
                      </Box>
                    </Paper>
                  ) : (
                    <Paper 
                      elevation={0}
                      sx={{ 
                        height: '100%',
                        maxHeight: '100%',
                        width: '100%',
                        minHeight: 0,
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: grey[50]
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Description sx={{ fontSize: 64, color: grey[300], mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          Select a requirement
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Choose a requirement from the list to view and edit details
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          startIcon={<Hub />}
          onClick={() => setGraphDialogOpen(true)}
          variant="contained"
          sx={{
            bgcolor: '#1565C0',
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              bgcolor: '#0D47A1'
            }
          }}
        >
          Graphs
        </Button>
        <Button 
          startIcon={<Analytics />}
          onClick={handleOpenSmartDialog}
          variant="contained"
          sx={{
            bgcolor: grey[900],
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              bgcolor: grey[800]
            }
          }}
        >
          Run SMART Analysis
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>

      {/* Graph View Dialog */}
      <GraphViewDialog
        open={graphDialogOpen}
        onClose={() => setGraphDialogOpen(false)}
        fileId={analysis?.data?.file_id}
        focusRequirementId={selectedRequirementId}
        fileName={analysis?.data?.file_name}
      />

      {/* SMART Analysis Dialog */}
      <SmartAnalysisDialog
        open={smartDialogOpen}
        onConfirm={handleAnalyzeValidatedRequirements}
        onClose={handleCloseSmartDialog}
        savedValidatedCount={savedValidatedCount}
        unsavedValidatedCount={unsavedValidatedCount}
        totalRequirements={totalCount}
        analyzing={smartAnalyzing}
        error={smartError}
      />

      {/* Validation Error Dialog */}
      <Dialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'error.main'
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'rgba(211, 47, 47, 0.08)',
            borderBottom: '1px solid',
            borderColor: 'error.light',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pb: 2
          }}
        >
          <ErrorOutline sx={{ fontSize: 28, color: 'error.main' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.dark' }}>
              Cannot Save Changes
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Missing required justifications
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body2" sx={{ mb: 2.5, color: 'text.secondary' }}>
            You have overridden the following SMART criteria without providing justification.
            Please explain your reasoning for each override before saving.
          </Typography>
          
          <List disablePadding>
            {validationErrors.map((criterion, index) => (
              <ListItem
                key={index}
                sx={{
                  bgcolor: 'rgba(211, 47, 47, 0.08)',
                  borderRadius: 2,
                  mb: 1,
                  border: '1px solid',
                  borderColor: 'error.light',
                  py: 1.5
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Edit sx={{ color: 'error.main', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {criterion}
                    </Typography>
                  }
                  secondary="Justification required"
                  secondaryTypographyProps={{
                    sx: { fontSize: '0.75rem', color: 'error.dark' }
                  }}
                />
              </ListItem>
            ))}
          </List>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="caption">
              <strong>Why justification matters:</strong> Override justifications create an audit trail
              for quality assurance and help maintain assessment consistency across the team.
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button
            onClick={() => setValidationDialogOpen(false)}
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              py: 1.5,
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            Got it, I'll add justifications
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AnalysisDialog;
